/**
 * 应用入口（Web / Electron 渲染层共用）
 */
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import ElementPlus from 'element-plus'
import * as ElementPlusIcons from '@element-plus/icons-vue'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import App from './App.vue'
import router from './router'
import { setNavigateHandler, setToastHandler } from './api/xajax'
import { toast } from './composables/useToast'
import { applyThemeToDocument } from './utils/theme-dom'
// #ifdef ELECTRON
import { installAiAnalyzeHandler } from './api/aigen/PageAnalyzeClient'
// #endif
// #ifdef WEB
import { initWebCacheGuard } from './utils/webCache'
// #endif
import './styles/theme.scss'
import './styles/common.scss'
// Element Plus：默认样式 → 深色变量 → 本项目主题覆盖（顺序不能变，覆盖必须最后加载）
import 'element-plus/dist/index.css'
import 'element-plus/theme-chalk/dark/css-vars.css'
import './styles/element.scss'

const app = createApp(App)

app.use(createPinia())
app.use(router)
// 控件统一用 Element Plus（中文文案）
app.use(ElementPlus, { locale: zhCn })
// 图标：全局注册 @element-plus/icons-vue，页面里直接用 <Search /> 之类
for (const [key, comp] of Object.entries(ElementPlusIcons)) {
  app.component(key, comp)
}

/* 启动时先把主题打到 <html>：Element Plus 的深色主题依赖 html.dark 类名 */
applyThemeToDocument((localStorage.getItem('niuren-theme') as 'light' | 'dark') || 'light')

/* api 层的副作用出口：交给路由与全局提示，避免 api 反向依赖 UI */
setNavigateHandler((url: string) => {
  router.push(url).catch(() => {
    /* ignore */
  })
})
setToastHandler((msg: string) => toast.info(msg))

// #ifdef ELECTRON
/* 装上「引擎请求 AI 分析页面」的处理器：登录 / 发布卡住时自动分析一次，结果记下来复用 */
installAiAnalyzeHandler()
// #endif

// #ifdef WEB
/* Web 端缓存治理：启动时比对 version.json，发现新版本提示 / 静默强刷 */
initWebCacheGuard()
// #endif

/**
 * 挂载兜底：启动阶段一旦抛错（比如桌面端产物被部署到浏览器、桥接缺失），
 * 至少在页面上显示原因，而不是留一个白屏让人无从下手。
 */
try {
  app.mount('#app')
} catch (e: any) {
  const msg = e?.message || String(e || '未知错误')
  console.error('[启动失败]', e)
  const el = document.getElementById('app')
  if (el) {
    el.innerHTML =
      '<div style="padding:32px;font:14px/1.8 system-ui,-apple-system,Segoe UI,sans-serif;color:#c45656">' +
      '<h3 style="margin:0 0 8px">页面启动失败</h3>' +
      `<div>${msg.replace(/[<>&]/g, '')}</div>` +
      '<div style="margin-top:12px;color:#666">若这是部署到服务器的 Web 版，请确认上传的是 ' +
      '<code>npm run build:web</code> 产出的 <code>dist/web</code>，而不是桌面端的 <code>dist/renderer</code>。</div>' +
      '</div>'
  }
}
